import { memo, useState, useCallback, useEffect, type CSSProperties } from 'react'
import { antennaElementTypesApi } from '../../lib/api.js'
import { COLLECTION_PRESET_COLORS } from '../../lib/catalog-colors.js'
import type { AntennaElementType } from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import '../programs/ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface AntennaElementTypeModalProps {
  channelId:   string
  elementType?: AntennaElementType
  onCreated?:  (elementType: AntennaElementType) => void
  onUpdated?:  (elementType: AntennaElementType) => void
  onClose:     () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const AntennaElementTypeModal = memo(function AntennaElementTypeModal({ channelId, elementType, onCreated, onUpdated, onClose }: AntennaElementTypeModalProps) {
  const isEdit = !!elementType
  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  const [label,   setLabel]   = useState(elementType?.label ?? '')
  const [color,   setColor]   = useState(elementType?.color ?? COLLECTION_PRESET_COLORS[0].value)

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t) }, [])
  const handleClose = () => { setVisible(false); setTimeout(onClose, 200) }

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) return
    setSaving(true); setError(null)
    try {
      if (isEdit && elementType) {
        const updated = await antennaElementTypesApi.update(elementType.id, { label: label.trim(), color })
        onUpdated?.(updated)
      } else {
        const created = await antennaElementTypesApi.create({ channelId, label: label.trim(), color })
        onCreated?.(created)
      }
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }, [channelId, isEdit, elementType, label, color, onCreated, onUpdated])

  return (
    <ModalShell
      title={isEdit ? 'Modifier le type d’élément d’antenne' : 'Nouveau type d’élément d’antenne'}
      size="narrow"
      visible={visible}
      onClose={handleClose}
    >
        <form onSubmit={handleSubmit} className="tp-modal-form">
          {error && (
            <div className="tp-feedback tp-feedback--error" role="alert">
              {error}
            </div>
          )}

          <div className="tp-form-field">
            <label className="tp-field-label" htmlFor="antenna-element-type-label">Nom *</label>
            <input id="antenna-element-type-label" className="tp-input" value={label} onChange={e => setLabel(e.target.value)} required autoFocus placeholder="Ex : Habillage météo, Générique de fin…" />
          </div>

          {elementType?.builtin && (
            <p className="catalog-field__hint">
              Type d’origine : son nom se change librement, mais il ne peut être ni
              désactivé ni supprimé — la diffusion s’y réfère.
            </p>
          )}

          <fieldset className="tp-form-field">
            <legend className="tp-field-label">Couleur</legend>
            <div className="catalog-color-options">
              {COLLECTION_PRESET_COLORS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={`catalog-color-option${color === option.value ? ' is-selected' : ''}`}
                  style={{ '--catalog-accent': option.value } as CSSProperties}
                  aria-label={option.label}
                  aria-pressed={color === option.value}
                />
              ))}
            </div>
          </fieldset>

          <div className="tp-modal-actions">
            <button type="button" onClick={handleClose} className="tp-button">
              Annuler
            </button>
            <button type="submit" disabled={saving || !label.trim()} className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined}>
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
    </ModalShell>
  )
})
